import { CSS } from '@stitches/react'
import { AreaSPLine } from 'Components/Elements/Chart/AreaSPLine'
import { Options, SeriesOptionsType } from 'highcharts'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { ThreatGroupData } from '../../../../features/home/routes/Home/types'
import { AreaSPLineData } from '../AreaSPLine/types'
import { ThreatGroupsStyled } from './TrendsAndThreatGroups.styled'
import { DrillDownPie, theme, useResponsive } from '@clickcease/cc-client-components-lib'
import { ThreatGroupBreakdownData } from 'Features/home/api/types'
import { NEW_ALLOWED_THREAT_GROUPS } from 'Features/home/routes/Home/constants'
import { PieTotalStyled, PieWrapperStyled } from '../Pie/Pie.styled'
import { commaSeparatedNumber } from 'Utils/number'

export const NewTrendsAndThreatGroups = ({
  css,
  children,
  chartCSS,
  threatGroupsData,
  areaSPLineData,
  AreaSPLineOptions,
}: {
  css?: CSS
  children?: React.ReactNode
  chartCSS?: CSS
  pieCSS?: CSS
  areaSPLineData: AreaSPLineData
  threatGroupsData: ThreatGroupBreakdownData[]
  AreaSPLineOptions?: Options
  pieOptions?: Options
  pieSize?: number
}) => {
  const { t } = useTranslation('home')
  const { windowWidth } = useResponsive()
  const { isDesktop } = useResponsive()

  let data: ThreatGroupData[] = []
  let totalInvalidCurrentPeriod = 0

  if (threatGroupsData && !(threatGroupsData instanceof Error)) {
    totalInvalidCurrentPeriod = threatGroupsData.reduce(
      (acc: number, item: ThreatGroupBreakdownData) => acc + item.invalid,
      0
    )
    data = threatGroupsData.reduce((acc: ThreatGroupData[], item: ThreatGroupBreakdownData) => {
      const existingItemIndex = acc.findIndex((element) => element.name === item.threatGroup)
      if (existingItemIndex !== -1) {
        acc[existingItemIndex].drilldownData[item.threatGroup] += item.invalid
        acc[existingItemIndex].drilldownData.data.push([item.threatType, item.invalid])
        acc[existingItemIndex].drilldownData.dataObj[item.threatType] = item.invalid
        acc[existingItemIndex].y += item.invalid
      } else {
        const threatGroupColor = Object.values(NEW_ALLOWED_THREAT_GROUPS).find(
          (group) => group.value === item.threatGroup
        )?.color
        const newItem = {
          color: threatGroupColor,
          name: item.threatGroup,
          y: item.invalid,
          drilldown: item.threatGroup,
          drilldownData: {
            type: 'pie',
            name: item.threatGroup,
            id: item.threatGroup,
            data: [[item.threatType, item.invalid]],
            dataObj: {
              [item.threatType]: item.invalid,
            },
          },
        } as ThreatGroupData
        acc.push(newItem)
      }
      return acc
    }, [])
  }
  const chartOptions: Options = {
    series: [
      {
        name: 'Threat Group',
        colorByPoint: true,
        type: 'pie',
        size: isDesktop ? '200' : '160',
        innerSize: '65%',
        data,
      } as SeriesOptionsType,
    ],
    chart: {
      type: 'pie',
      height: null,
    },
    legend: {
      symbolHeight: 14,
      symbolWidth: 14,
      itemMarginBottom: 12,
      itemHoverStyle: { cursor: 'pointer' },
      alignColumns: true,
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      itemStyle: {
        color: theme.colors.black.value,
        fontSize: theme.fontSizes.md.value,
        fontWeight: theme.fontWeights.l.value,
      },
    },
    tooltip: {
      enabled: true,
      outside: false,
      useHTML: true,
      style: {
        zIndex: 100,
      },
    },
  }

  return (
    <ThreatGroupsStyled css={css}>
      {windowWidth && (
        <AreaSPLine chartCSS={chartCSS} data={areaSPLineData} options={AreaSPLineOptions} />
      )}
      {windowWidth && !!data && !(data instanceof Error) && (
        <PieWrapperStyled>
          <DrillDownPie
            options={chartOptions}
            chartCSS={{ maxWidth: '100%', height: '100%' }}
            data={data}
            drillDownSeries={data.map((item) => item.drilldownData)}
          />
          {chartOptions && (
            <PieTotalStyled>
              {t('total_threats')} <span>{commaSeparatedNumber(totalInvalidCurrentPeriod)}</span>
            </PieTotalStyled>
          )}
        </PieWrapperStyled>
      )}
      {children}
    </ThreatGroupsStyled>
  )
}
